import React, { Fragment, useState, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { XMarkIcon, EnvelopeIcon, LinkIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import docEditorServiceApi from "../../services/doc-editor-service-api";
import userServiceApi from "../../services/user-service-api";
import docProfileService from "../../services/docprofile-service";
import docSearchService from "../../services/docsearch-service-api";
import dashboardOrchestrationService from "../../services/dashboard-orchestration-service-api";
import { readCookie, isUserLoggedIn, getQueryParam, DecryptedString, normalizeGuid } from "../../utils/helpers";
import { DEFAULT_USER_GUID, EMPTY_GUID } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { ROUTE_CONSTANT } from "../../routing/route-constants";
import { CategoryName } from "../../utils/constants";
import { FilesBlobPath, ShareSimpliMaxLimit } from "../../utils/general-config";
import { Domain } from "@material-ui/icons";

const ShareWithOthersModal = ({ show, toggleShareWithOthersModal }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("email");
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [emailLimitError, setEmailLimitError] = useState("");
  const [isEmailDisabled, setIsEmailDisabled] = useState(false);
  const [isLinkDisabled, setIsLinkDisabled] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [randomShareGuid, setRandomShareGuid] = useState("");

  const unqn = getQueryParam("unqn");
  const documentId = getQueryParam("documentid") || getQueryParam("docId");
  const effectiveDocId = unqn || documentId;
  const urlFileName = getQueryParam("ofn");

  const userGuid =
    isUserLoggedIn() && readCookie("userguid") ? readCookie("userguid") : "a000000000000000000000000000000a";

  const baseBlobUrl = FilesBlobPath();
  const fallbackDocUrl = `${baseBlobUrl}/${userGuid}/${effectiveDocId}`;
  const [docDetails, setDocDetails] = useState({
    name: urlFileName || "Untitled Document",
    folderId: userGuid,
    docUrl: fallbackDocUrl,
  });

  const isProfileFetched = useRef(false);
  const isLimitReached = !isUserLoggedIn() && emails.length >= ShareSimpliMaxLimit;

  // Effect: Fetch filename from DocProfile if 'ofn' is missing and User is Signed In
  useEffect(() => {
    const generateGUID = () => crypto.randomUUID();
    const guid = generateGUID();
    setRandomShareGuid(guid);
    const domain = window.location.origin;
    const shUrl =
      isUserLoggedIn() && readCookie("userguid") ? `${domain}/app/t/v?a=${guid}` : `${domain}/app/t/v?u=${guid}`;
    setShareUrl(shUrl);
    const fetchDocProfile = async () => {
      if (isProfileFetched.current) return;

      if (effectiveDocId && isUserLoggedIn() && userGuid !== EMPTY_GUID && userGuid !== DEFAULT_USER_GUID) {
        try {
          isProfileFetched.current = true;
          const response = await docProfileService.GetDocProfile(userGuid, effectiveDocId);
          console.log("GetDocProfile Response:", response);
          const data = response?.data || response;
          if (data) {
            const fetchedName = data.modifiedName || "Untitled Document";

            let fetchedUrl = data.modifiedFileUrl || fallbackDocUrl;
            let fetchedFolderId = data.folderId || userGuid;

            const matches = fetchedUrl.match(/\/pdfsimpliusers\/([^\/]+)\//);
            if (matches && matches[1]) {
              fetchedFolderId = matches[1];
            }

            setDocDetails({
              name: fetchedName,
              folderId: fetchedFolderId,
              docUrl: fetchedUrl,
            });
          }
        } catch (error) {
          console.error("Failed to fetch document profile:", error);
        }
      }
    };

    fetchDocProfile();
  }, [effectiveDocId, userGuid, fallbackDocUrl]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && emailInput.trim() !== "") {
      e.preventDefault();
      if (!isUserLoggedIn() && emails.length >= ShareSimpliMaxLimit) {
        setEmailLimitError(t("share_simpli.max_limit_error_unauth"));
        setEmailInput("");
        return;
      }

      setEmails([...emails, emailInput.trim()]);
      setEmailInput("");
      setEmailLimitError("");
    }
  };

  const removeEmail = (index) => {
    setEmails(emails.filter((_, i) => i !== index));
    setEmailLimitError("");
  };

  const handleShare = async () => {
    setIsLoading(true);

    try {
      const currentUserGuid =
        isUserLoggedIn() && readCookie("userguid")
          ? readCookie("userguid").replaceAll("-", "")
          : "a000000000000000000000000000000a";
      const guid = () => {
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
          (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
        );
      };
      const finalEmails = emailInput.trim() !== "" ? [...emails, emailInput.trim()] : emails;
      const currentTimestamp = new Date().toISOString();

      const fileUrl = effectiveDocId ? `${currentUserGuid}/${effectiveDocId}` : "";

      if (!isUserLoggedIn() || currentUserGuid === EMPTY_GUID) {
        if (finalEmails.length > ShareSimpliMaxLimit) {
          setEmailLimitError(t("share_simpli.max_limit_error_unauth"));
          setIsLoading(false);
          return;
        }

        const payload = {
          userGuid: EMPTY_GUID,
          queryData: JSON.stringify([
            {
              filename: docDetails.name,
              unqn: effectiveDocId || "",
              url: fileUrl,
              category: "sharesimpli",
              sendToUser: finalEmails,
              shareType: activeTab == "email" ? "EMAIL" : "LINK",
              message: message,
              domain: window.location.origin,
              disableDownloadForEmail: isEmailDisabled,
              disableDownloadForLink: isLinkDisabled,
              ...(activeTab !== "email" && { shareLink: shareUrl }),
              ...(activeTab !== "email" && { randomGuid: randomShareGuid }),
            },
          ]),
          category: "sharesimpli",
          visitId: readCookie("visitGuid") || "",
          visitorId: readCookie("visitorGuid") || "",
        };

        const response = await docEditorServiceApi.SaveEditorQueryInfo(payload);

        toggleShareWithOthersModal(false);

        const signupUrl = `${ROUTE_CONSTANT.SIGNUP}?querydata=${response}&cname=${CategoryName.SHARE_SIMPLI.toLowerCase()}`;
        navigate(signupUrl);

        return;
      }

      const finalDocName = docDetails.name;
      const finalDocFolder = docDetails.folderId || currentUserGuid;
      const finalDocUrl = docDetails.docUrl || fallbackDocUrl;

      let permissionType = "Viewer";
      // check email and sharelink permission
      if (activeTab == "email") {
        if (isEmailDisabled) {
          permissionType = "Download";
        }
      } else {
        if (isLinkDisabled) {
          permissionType = "Download";
        }
      }
      const bulkPayload = finalEmails.map((email) => ({
        id: guid(),
        docId: effectiveDocId || "",
        userId: normalizeGuid(currentUserGuid),
        groupId: EMPTY_GUID,
        orgId: EMPTY_GUID,
        sharedWith: EMPTY_GUID,
        permissionType: permissionType, //"Viewer",
        typeOfDoc: "PDF_DOC",
        changeType: "ADD",
        portalCode: "PSSH",
        docName: finalDocName,
        isStarred: false,
        locale: "",
        docFolder: finalDocFolder,
        docUrl: finalDocUrl,
        docStatus: "NONE",
        createdOn: currentTimestamp,
        lastModifiedOn: currentTimestamp,
        sourceOperationTimeStamp: "0001-01-01T00:00:00+00:00",
        shareEmail: email,
        shareType: activeTab == "email" ? "EMAIL" : "LINK",
        shareMessage: message || "",
        isEmailSent: activeTab == "email" ? true : false,
        shareLink: shareUrl,
        IsActive: true,
        RandomGuid: randomShareGuid,
      }));
      try {
        await docSearchService.UpdateDocSearchBulk(EMPTY_GUID, bulkPayload);
        const userProfileResponse = await userServiceApi.getUserProfileByGuid(currentUserGuid, true);
        const abridgedResponse = userProfileResponse?.data?.result || userProfileResponse?.data || userProfileResponse;

        let isPremium = false;

        if (!!abridgedResponse?.perquisiteBountyKey) {
          const decryptVal = DecryptedString(
            abridgedResponse.perquisiteBountyKey,
            abridgedResponse?.userGuid?.replaceAll("-", ""),
          );

          isPremium = decryptVal?.toLowerCase() === "true";
        }

        if (isPremium) {
          console.log("Paid user detected, sending orchestration emails...");

          const emailPayload = {
            userGuid: currentUserGuid,
            docGuid: effectiveDocId,
          };

          await dashboardOrchestrationService.sendShareEmails(emailPayload);
          toggleShareWithOthersModal(false);
          navigate(ROUTE_CONSTANT.DASHBOARD_TRANSFER);
        } else {
          // Free user → Billing
          toggleShareWithOthersModal(false);
          const billingUrl = ROUTE_CONSTANT.BILLING_TRANSFER_LOCALE.replace(":locale", "en");
          navigate(billingUrl);
        }
      } catch (error) {
        console.error("DocSearch bulk update failed", error);
      }
    } catch (error) {
      console.error("Share failed:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleEmailDisable = (e) => {
    const checked = e.target.checked;
    setIsEmailDisabled(checked);
    console.log("Email Disabled:", checked);
  };
  const handleLinkDisable = (e) => {
    const checked = e.target.checked;
    setIsLinkDisabled(checked);
    console.log("Link Disabled:", checked);
  };
  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => toggleShareWithOthersModal(false)}>
        {/* Background Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[linear-gradient(0deg,_#FFF_10.95%,_rgba(255,255,255,0)_90.38%),_linear-gradient(166deg,_#FFE1CC_-68.53%,_#FFCCF7_-5.43%,_#D2F4FF_49.41%)] opacity-100" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-[650px] transform overflow-hidden rounded-[14px] bg-white text-left align-middle shadow-2xl transition-all border-2 border-[#FFE1CC]">
                <Dialog.Title className="text-[18px] md:text-[24px] px-[30px] py-[20px] font-bold text-[#2D3748] border-b border-[#F0F0F0]">
                  Share with others
                </Dialog.Title>
                <div className="p-[30px] pt-[20px]">
                  {/* Tabs */}
                  <div className="flex border-b border-gray-100 mb-4">
                    <button
                      onClick={() => setActiveTab("email")}
                      className={`flex items-center justify-center gap-2 p-2 px-4 text-sm font-medium transition-all ${activeTab === "email"
                          ? "text-[#16283D] border-b-2 border-[#4A90E2] bg-[#E7F1FF]"
                          : "text-gray-400 border-b-2 border-transparent"
                        }`}
                      id="email_btn_id"
                    >
                      <EnvelopeIcon className="h-4 w-4 text-[#4A90E2]" />
                      Email
                    </button>
                    <button
                      onClick={() => setActiveTab("link")}
                      className={`flex items-center justify-center gap-2 p-2 px-4 text-sm font-medium transition-all ${activeTab === "link"
                          ? "text-[#16283D] border-b-2 border-[#4A90E2] bg-[#E7F1FF]"
                          : "text-gray-400 border-b-2 border-transparent"
                        }`}
                      id="advanced_link_btn_id"
                    >
                      <LinkIcon className="h-4 w-4" />
                      Advanced link
                    </button>
                  </div>

                  {activeTab === "email" ? (
                    <div className="">
                      <div>
                        <label className="block text-sm font-bold text-[#2D3748] mb-3">File for sharing:</label>
                        <div className="w-full p-4 bg-[#F8F9FA] rounded-[6px] text-[#A0AEC0] text-sm">
                          {/* Use docDetails name */}
                          {docDetails.name}
                        </div>
                      </div>
                      {/* Recipient Input */}

                      <div className="pt-[15px]">
                        <label className="block text-sm font-bold text-[#2D3748] mb-0.5">Send to people</label>
                        <p className="text-xs text-[#718096] mb-3 font-medium">Enter email address</p>

                        <div className="border-2 border-[#E7F1FF] rounded-lg p-3 min-h-[92px] focus-within:ring-1 focus-within:ring-blue-300 transition-shadow">
                          <div className="flex flex-col gap-2">
                            <input
                              type="text"
                              disabled={isLimitReached}
                              className="flex-1 outline-none text-sm min-w-[150px] placeholder:text-gray-300 disabled:cursor-not-allowed disabled:bg-transparent"
                              value={emailInput}
                              onChange={(e) => setEmailInput(e.target.value)}
                              onKeyDown={handleKeyDown}
                              id="send_to_people_id"
                            />
                            <div className="flex items-start content-start gap-[5px] self-stretch flex-wrap [overflow-wrap:anywhere]">
                              {emails.map((email, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-1.5 bg-[#EBF4FF] text-[#4A5568] pl-2 pr-1 py-1 rounded-md text-sm border border-[#D1E9FF]"
                                >
                                  <UserCircleIcon className="h-4 w-4 text-[#4A90E2]" />
                                  <span>{email}</span>
                                  <button
                                    onClick={() => removeEmail(index)}
                                    className="hover:bg-blue-100 rounded-full p-0.5"
                                  >
                                    <XMarkIcon className="h-3 w-3 text-gray-400" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Message */}

                      <textarea
                        placeholder="Add message (optional)"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        id="add_message_id"
                        className="w-full border-2 border-[#E7F1FF] rounded-lg p-4 text-sm outline-none focus:ring-1 focus:ring-blue-300 min-h-[87px] resize-none placeholder:text-gray-400 mt-[10px]"
                      />

                      {(isLimitReached || emailLimitError) && (
                        <div className="w-full flex justify-center mt-2">
                          <p className="text-red-500 text-sm font-medium">
                            {emailLimitError || t("share_simpli.max_limit_error_unauth")}
                          </p>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                          checked={isEmailDisabled}
                          onChange={handleEmailDisable}
                          id="disable_download_id"
                        />
                        <span className="text-[#A0AEC0] text-sm">Disable Download</span>
                      </div>
                      {/* Footer Buttons */}

                      <div className="flex flex-col gap-[10px] md:flex-row md:gap-4 justify-between items-center pt-4">
                        <button
                          onClick={() => toggleShareWithOthersModal(false)}
                          id="cancel_btn_id"
                          className="order-2 md:order-1 w-full md:w-auto px-[40px] py-[15px] bg-[#E2E8F0] text-[#718096] font-bold rounded-full hover:bg-gray-300 transition-colors uppercase text-sm tracking-widest"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleShare}
                          id="send_btn_id"
                          disabled={!emails.length && !emailInput.trim()}
                          className="order-1 md:order-2 w-full md:w-auto px-[40px] py-[15px] bg-gradient-to-r from-[#FF7E47] to-[#FF9040] text-white font-bold rounded-full shadow-lg hover:shadow-orange-200 transition-all uppercase text-sm tracking-widest"
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-8">
                      <div className="space-y-4">
                        {/* Temporary Link Row */}
                        {/* <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                          />
                          <span className="text-[#A0AEC0] text-sm">Send temporary link. Expire after</span>
                          <input
                            type="number"
                            defaultValue="1"
                            className="w-14 border border-gray-200 rounded px-2 py-1 text-sm text-gray-500 text-center"
                          />
                          <select className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-500 bg-white">
                            <option>Day</option>
                            <option>Hour</option>
                          </select>
                        </div> */}
                        {/* Disable Download Row */}
                        {/* <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                          />
                          <span className="text-[#A0AEC0] text-sm">Disable Download</span>
                        </div> */}
                      </div>
                      {/* Action Button */}
                      {/* <div className="flex justify-center">
                        <button
                          onClick={handleShare}
                          className="px-8 py-2.5 bg-[#8EADFF] text-white font-medium rounded-md hover:bg-blue-400 transition-colors"
                        >
                          Generate Link
                        </button>
                      </div> */}
                      {/* Link Field */}
                      <div className="flex flex-col sm:flex-row sm:items-stretch gap-3 sm:gap-4 rounded-md p-1 w-full">
                        <input
                          readOnly
                          value={shareUrl}
                          id="shareUrl"
                          className="w-full flex-1 px-4 py-2.5 text-sm text-gray-400 bg-white outline-none border border-gray-200 rounded-md [overflow-wrap:anywhere]"
                          onClick={handleShare}
                        />

                        <button
                          className="w-full sm:w-auto px-4 py-2.5 text-sm bg-[#0E76FF] text-white rounded-md hover:bg-blue-600 transition-colors whitespace-nowrap"
                          onClick={handleShare}
                          id="copy_link_id"
                        >
                          Copy link
                        </button>
                      </div>

                      {/* Upgrade Notice */}
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isLinkDisabled}
                          onChange={handleLinkDisable}
                          id="disable_download_id_link"
                          className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                        />
                        <span className="text-[#A0AEC0] text-sm">Disable Download</span>
                      </div>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ShareWithOthersModal;
